import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { Bar, Line } from "react-chartjs-2";
import "chart.js/auto";
import { TailSpin } from "react-loader-spinner";
import "./monthwiseOTPcountV2.css";

const API_URL = process.env.REACT_APP_API_MonthWiseOTPCount;

const MonthWiseOTPCountV2 = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chartType, setChartType] = useState("bar");

  const fetchMonthlyData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(API_URL);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching data", error);
      setData(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMonthlyData();
  }, []);

  const months = useMemo(
    () => (data ? data.map((item) => item.month_name) : []),
    [data]
  );

  const counts = useMemo(
    () => (data ? data.map((item) => item.count) : []),
    [data]
  );

  const totalCount = useMemo(
    () => (counts?.length ? counts.reduce((a, c) => a + c, 0) : 0),
    [counts]
  );

  const chartData = {
    labels: months,
    datasets: [
      {
        label: "OTP Count",
        data: counts,
        backgroundColor: "rgba(56, 189, 248, 0.7)",
        borderColor: "rgba(14, 165, 233, 1)",
        borderWidth: 1,
        borderRadius: 6,
        fill: chartType === "line",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", labels: { font: { size: 12 } } },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxRotation: 45, font: { size: 11 } },
      },
      y: {
        grid: { color: "#f1f5f9" },
        ticks: {
          font: { size: 11 },
          callback: (v) => (v >= 1000 ? `${v / 1000}k` : v),
        },
      },
    },
  };

  return (
    <div className="month-otp-v2">
      <div className="month-otp-v2-header">
        <h1 className="month-otp-v2-title">Month-wise OTP Statistics</h1>
        <select
          className="month-otp-v2-select"
          value={chartType}
          onChange={(e) => setChartType(e.target.value)}
        >
          <option value="bar">Bar Chart</option>
          <option value="line">Line Chart</option>
        </select>
      </div>

      {totalCount > 0 && (
        <div className="month-otp-v2-card">
          <div className="month-otp-v2-stat-label">Total OTPs Across All Months</div>
          <div className="month-otp-v2-stat-value">{totalCount.toLocaleString()}</div>
        </div>
      )}

      <div className="month-otp-v2-main">
        <div className="month-otp-v2-chart-card">
          {loading ? (
            <div className="month-otp-v2-loading">
              <TailSpin
                height={48}
                width={48}
                color="#38bdf8"
                ariaLabel="loading"
              />
            </div>
          ) : (
            <div className="month-otp-v2-chart-wrapper">
              {chartType === "bar" ? (
                <Bar data={chartData} options={chartOptions} />
              ) : (
                <Line
                  data={{
                    ...chartData,
                    datasets: [
                      {
                        ...chartData.datasets[0],
                        fill: true,
                        tension: 0.3,
                        backgroundColor: "rgba(56, 189, 248, 0.2)",
                        borderColor: "rgba(14, 165, 233, 1)",
                        borderWidth: 2,
                      },
                    ],
                  }}
                  options={chartOptions}
                />
              )}
            </div>
          )}
        </div>

        <div className="month-otp-v2-table-card">
          <div className="month-otp-v2-table-wrapper">
            <table className="month-otp-v2-table">
              <thead>
                <tr>
                  <th>Month Name</th>
                  <th>OTP Count</th>
                </tr>
              </thead>
              <tbody>
                {(data || []).map((row) => (
                  <tr key={row.month_name}>
                    <td>{row.month_name}</td>
                    <td className="month-otp-v2-count">
                      {row.count.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthWiseOTPCountV2;
